import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  DownloadOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import axios from "axios";
import { getDate as getIndiaDate } from "../../utils/reportUtils";
import LatestUploadCard from "../LatestUploadCard";
import { excelDateToDate } from "../../utils/excelDates";

const API = process.env.REACT_APP_BACKEND_URL;

const PR_COLUMNS = [
  { header: "PR Number", field: "purchaseRequisition", required: true },
  { header: "Item Code", field: "itemCode" },
  { header: "PR Creation Date", field: "requisitionDate", type: "date" },
  { header: "Material Code", field: "material", required: true },
  { header: "Material Description", field: "shortText" },
  { header: "PR Qty", field: "quantityRequested", type: "number", required: true },
];

const PO_COLUMNS = [
  { header: "PO Number", field: "purchasingDocument", required: true },
  { header: "PR Number", field: "purchaseRequisition" },
  { header: "Item Code", field: "itemCode" },
  { header: "Material Code", field: "material", required: true },
  { header: "Material Description", field: "shortText" },
  { header: "PO Creation Date", field: "documentDate", type: "date" },
  { header: "PO Sent Date", field: "poSentDate", type: "date" },
  { header: "PO Delivery Date", field: "deliveryDate", type: "date" },
  { header: "PO Qty", field: "orderQuantity", type: "number", required: true },
  { header: "Supplier Name", field: "supplierName" },
  { header: "Supplier Email", field: "supplierEmail" },
];

const DELIVERY_COLUMNS = [
  { header: "GRN Number", field: "materialDocument", required: true },
  { header: "PO Number", field: "purchaseOrder", required: true },
  { header: "Material Code", field: "material", required: true },
  { header: "Material Description", field: "materialDescription" },
  {
    header: "Qty Received",
    field: "quantityInUnitOfEntry",
    type: "number",
    required: true,
  },
  { header: "GRN Date", field: "postingDate", type: "date" },
];

const normalize = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

// Headers match regardless of letter case and extra spaces ("PO number" = "PO Number").
const headerKey = (value) => normalize(value).toLowerCase().replace(/\s+/g, " ");

const isBlank = (value) =>
  value === null || value === undefined || value === "";

// Returns null for a blank cell and NaN for text that is not a number (reported as a row error).
// Handles SAP formats: trailing minus "100-", brackets "(100)" and "1.234,5" decimal commas.
const toNumber = (value) => {
  if (isBlank(value)) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;

  let text = String(value).replace(/\s/g, "");
  if (!text) return null;

  let negative = false;

  if (/^\(.*\)$/.test(text)) {
    negative = true;
    text = text.slice(1, -1);
  }

  if (text.endsWith("-")) {
    negative = !negative;
    text = text.slice(0, -1);
  }

  if (
    text.includes(",") &&
    text.includes(".") &&
    text.lastIndexOf(",") > text.lastIndexOf(".")
  ) {
    text = text.replace(/\./g, "").replace(",", ".");
  } else {
    text = text.replace(/,/g, "");
  }

  if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(text)) return NaN;

  const number = Number(text);
  return negative ? -number : number;
};


const formatDate = (value) => {
  if (isBlank(value)) return "—";

  const date = value instanceof Date ? value : excelDateToDate(value);
  if (!date || Number.isNaN(date.getTime())) return String(value);

  return `${String(date.getUTCDate()).padStart(2, "0")}-${String(
    date.getUTCMonth() + 1,
  ).padStart(2, "0")}-${date.getUTCFullYear()}`;
};

const displayValue = (value, column) => {
  if (isBlank(value)) return "—";
  if (column.type === "date") return formatDate(value);
  return String(value);
};

const makeTemplate = (columns, sheetName, fileName) => {
  const headers = columns.map((column) => column.header);
  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  worksheet["!cols"] = columns.map((column) => ({
    wch:
      column.header.length > 24 ? 26 : Math.max(14, column.header.length + 2),
  }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, fileName);
};

const getAuthConfig = () => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");
  if (!storedUser?.accessToken) throw new Error("You are not logged in.");
  return { headers: { Authorization: `Bearer ${storedUser.accessToken}` } };
};

// The most recent file name and upload time among rows that record their source file.
const getLatestSource = (rows) => {
  let latest = null;
  rows.forEach((row) => {
    if (!row.sourceUploadedAt) return;
    if (
      !latest ||
      new Date(row.sourceUploadedAt) > new Date(latest.sourceUploadedAt)
    ) {
      latest = row;
    }
  });
  return {
    fileName: latest?.sourceFileName || null,
    uploadedAt: latest?.sourceUploadedAt || null,
  };
};

// File each stored row came from, for the uploaded-files list.
const getRowSource = (row) => ({
  fileName: row.sourceFileName,
  uploadedAt: row.sourceUploadedAt,
});

const fetchLatestRows = async (endpoint, authConfig) => {
  const response = await axios.get(`${API}${endpoint}`, authConfig);
  return Array.isArray(response.data) ? response.data : [];
};

const PR_CONFIG = {
  title: "PR Report",
  description:
    "Upload PR lines. Each file adds new PR lines or updates existing ones; earlier lines are kept. A PR is closed once a PO in the PO report references its PR number.",
  columns: PR_COLUMNS,
  requiredFields: PR_COLUMNS.filter((column) => column.required).map(
    (column) => column.field,
  ),
  templateName: "PR_Report_Template.xlsx",
  sheetName: "PR Report",
  buildPayload: (rows, fileName) => ({
    endpoint: `/open-pr/upload?fileName=${encodeURIComponent(fileName)}`,
    body: rows.map((row) => ({
      purchaseRequisition: row.purchaseRequisition,
      itemCode: row.itemCode,
      requisitionDate: row.requisitionDate || null,
      material: row.material,
      shortText: row.shortText,
      quantityRequested: row.quantityRequested,
    })),
  }),
  loadLatest: async (authConfig) => {
    const rows = await fetchLatestRows("/open-pr/latest", authConfig);
    return {
      ...getLatestSource(rows),
      rows,
    };
  },
};

const PO_CONFIG = {
  title: "PO Report",
  description:
    "Upload PO lines. Each file adds new PO lines or updates existing ones; earlier lines are kept. A PO stays open until the GRN quantity received reaches the PO quantity.",
  columns: PO_COLUMNS,
  requiredFields: PO_COLUMNS.filter((column) => column.required).map(
    (column) => column.field,
  ),
  templateName: "PO_Report_Template.xlsx",
  sheetName: "PO Report",
  buildPayload: (rows, fileName) => ({
    endpoint: `/open-po/upload?fileName=${encodeURIComponent(fileName)}`,
    body: rows.map((row) => ({
      purchasingDocument: row.purchasingDocument,
      purchaseRequisition: row.purchaseRequisition,
      itemCode: row.itemCode,
      material: row.material,
      shortText: row.shortText,
      documentDate: row.documentDate || null,
      poSentDate: row.poSentDate || null,
      deliveryDate: row.deliveryDate || null,
      orderQuantity: row.orderQuantity,
      supplierName: row.supplierName,
      supplierEmail: row.supplierEmail,
    })),
  }),
  loadLatest: async (authConfig) => {
    const rows = await fetchLatestRows("/open-po/latest", authConfig);
    return {
      ...getLatestSource(rows),
      rows,
    };
  },
};

const DELIVERY_CONFIG = {
  title: "GRN Report",
  description:
    "Upload goods received (GRN) lines. Each file adds to the earlier ones; a line with the same GRN number, PO number and material code replaces the stored one. Qty Received is matched to the PO by PO number and material code.",
  columns: DELIVERY_COLUMNS,
  requiredFields: DELIVERY_COLUMNS.filter((column) => column.required).map(
    (column) => column.field,
  ),
  templateName: "GRN_Report_Template.xlsx",
  sheetName: "GRN Report",
  buildPayload: (rows, fileName) => ({
    endpoint: `/delivery-report/upload?fileName=${encodeURIComponent(fileName)}`,
    body: rows.map((row) => ({
      materialDocument: row.materialDocument,
      purchaseOrder: row.purchaseOrder,
      material: row.material,
      materialDescription: row.materialDescription,
      quantityInUnitOfEntry: row.quantityInUnitOfEntry,
      postingDate: row.postingDate || null,
    })),
  }),
  loadLatest: async (authConfig) => {
    const rows = await fetchLatestRows("/delivery-report/latest", authConfig);
    return {
      ...getLatestSource(rows),
      rows,
    };
  },
};

const EMPTY_LATEST = {
  rows: [],
  fileName: null,
  uploadedAt: null,
  uploadedBy: null,
};

function ExcelUploadPanel({ config }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [latest, setLatest] = useState(EMPTY_LATEST);
  const [latestLoading, setLatestLoading] = useState(true);
  const [latestError, setLatestError] = useState("");

  const loadLatest = async () => {
    try {
      setLatestLoading(true);
      setLatestError("");
      const result = await config.loadLatest(getAuthConfig());
      const dateColumns = config.columns.filter(
        (column) => column.type === "date",
      );
      setLatest({
        fileName: result.fileName,
        uploadedAt: result.uploadedAt,
        uploadedBy: result.uploadedBy || null,
        rows: result.rows.map((row, index) => {
          const next = { ...row, id: index + 1 };
          // Backend dates are India-time timestamps; keep only the India date.
          dateColumns.forEach((column) => {
            next[column.field] = getIndiaDate(row[column.field]);
          });
          return next;
        }),
      });
    } catch (latestLoadError) {
      setLatest(EMPTY_LATEST);
      setLatestError(
        latestLoadError?.response?.data?.Exception ||
          latestLoadError?.message ||
          "Unable to load the latest uploaded data.",
      );
    } finally {
      setLatestLoading(false);
    }
  };

  useEffect(() => {
    loadLatest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const clear = () => {
    setFile(null);
    setRows([]);
    setError("");
    setSuccess("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadTemplate = () => {
    setError("");
    setSuccess("");
    makeTemplate(config.columns, config.sheetName, config.templateName);
    setSuccess(`${config.title} template downloaded.`);
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setError("");
    setSuccess("");
    setRows([]);
    setFile(null);
    setLoading(true);

    try {
      const lowerName = selectedFile.name.toLowerCase();
      if (!lowerName.endsWith(".xlsx") && !lowerName.endsWith(".xls")) {
        throw new Error("Please upload an Excel file (.xlsx or .xls).");
      }

      const workbook = XLSX.read(await selectedFile.arrayBuffer(), {
        type: "array",
      });
      if (!workbook.SheetNames.length) {
        throw new Error("The Excel file does not contain a sheet.");
      }

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const headerRows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
        raw: true,
      });
      if (!headerRows.length)
        throw new Error("The uploaded Excel sheet is empty.");

      const actualHeaders = headerRows[0].map(headerKey);
      const expectedHeaders = config.columns.map((column) => column.header);
      const usedHeaderCounts = {};
      const columnIndices = expectedHeaders.map((header) => {
        const occurrence = usedHeaderCounts[header] || 0;
        usedHeaderCounts[header] = occurrence + 1;

        const matchingIndices = [];
        actualHeaders.forEach((actualHeader, index) => {
          if (actualHeader === headerKey(header)) matchingIndices.push(index);
        });

        return matchingIndices[occurrence] ?? -1;
      });

      const missingHeaders = expectedHeaders.filter(
        (header, index) => columnIndices[index] === -1,
      );
      if (missingHeaders.length) {
        throw new Error(
          `The Excel file is missing these columns (or duplicate occurrences): ${missingHeaders.join(", ")}. Download the template to get the exact headings.`,
        );
      }

      const rawRows = headerRows.slice(1);
      if (!rawRows.length)
        throw new Error("The Excel file contains headers but no data rows.");

      const date1904 = Boolean(workbook.Workbook?.WBProps?.date1904);
      // Cells that have a value which is not a valid number/date, reported with the Excel row number
      const invalidCells = [];

      const convertedRows = rawRows
        .map((excelRow, index) => {
          const row = { id: index + 1 };
          config.columns.forEach((column, columnIndex) => {
            const value = excelRow[columnIndices[columnIndex]];
            if (column.type === "date") {
              const date = excelDateToDate(value, date1904);
              if (!date && normalize(value)) {
                invalidCells.push(
                  `Row ${index + 2}: ${column.header} "${normalize(value)}" is not a valid date.`,
                );
              }
              row[column.field] = date;
            } else if (column.type === "number") {
              const number = toNumber(value);
              if (Number.isNaN(number)) {
                invalidCells.push(
                  `Row ${index + 2}: ${column.header} "${normalize(value)}" is not a valid number.`,
                );
              }
              row[column.field] = Number.isNaN(number) ? null : number;
            } else row[column.field] = normalize(value);
          });
          return row;
        })
        .filter((row) =>
          config.columns.some((column) => !isBlank(row[column.field])),
        );

      if (!convertedRows.length)
        throw new Error("The Excel file does not contain any data rows.");

      const validationErrors = [...invalidCells];
      convertedRows.forEach((row, index) => {
        config.requiredFields.forEach((field) => {
          if (isBlank(row[field])) {
            const column = config.columns.find((item) => item.field === field);
            validationErrors.push(
              `Row ${index + 2}: ${column?.header || field} is required.`,
            );
          }
        });
      });
      if (validationErrors.length) {
        throw new Error(validationErrors.slice(0, 15).join("\n"));
      }

      setFile(selectedFile);
      setRows(convertedRows);
      setSuccess(
        `${convertedRows.length} ${config.title.toLowerCase()} row(s) loaded successfully.`,
      );
    } catch (uploadError) {
      setError(uploadError?.message || "Unable to read the Excel file.");
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!file || !rows.length) {
      setError(`Please choose a ${config.title} Excel file first.`);
      return;
    }

    setError("");
    setSuccess("");
    setUploading(true);

    try {
      const authConfig = getAuthConfig();
      const result = config.buildPayload(rows, file.name);
      const response = await axios.post(
        `${API}${result.endpoint}`,
        result.body,
        authConfig,
      );
      const message =
        typeof response.data === "string"
          ? response.data
          : `Reports uploaded successfully. ${response.data?.recordCount ?? 0} record(s) created.`;
      setSuccess(message);
      // Show what is now stored instead of the submitted preview.
      setFile(null);
      setRows([]);
      await loadLatest();
    } catch (uploadError) {
      setError(
        uploadError?.response?.data?.Exception ||
          uploadError?.response?.data?.message ||
          uploadError?.response?.data ||
          uploadError?.message ||
          "Report upload failed.",
      );
    } finally {
      setUploading(false);
    }
  };

  const gridColumns = useMemo(
    () =>
      config.columns.map((column) => ({
        field: column.field,
        headerName: column.header,
        minWidth: column.header.length > 22 ? 180 : 130,
        flex: column.header.length > 22 ? 1.5 : 1,
        valueFormatter: (value) => displayValue(value, column),
      })),
    [config],
  );

  return (
    <Box>
      {(error || success) && (
        <Alert
          severity={error ? "error" : "success"}
          sx={{ mb: 2, whiteSpace: "pre-line" }}
          onClose={() => {
            setError("");
            setSuccess("");
          }}
        >
          {error || success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <LatestUploadCard
          fileName={latest.fileName}
          uploadedAt={latest.uploadedAt}
          uploadedBy={latest.uploadedBy}
          loading={latestLoading}
          error={latestError}
          isEmpty={latest.rows.length === 0}
          emptyMessage={`No ${config.title.toLowerCase()} has been uploaded yet.`}
          onRefresh={loadLatest}
          rows={latest.rows}
          columns={gridColumns}
          getSource={getRowSource}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{ p: 3, border: "1px solid #e3e7ef", borderRadius: 2, mb: 3 }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              {config.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {config.description}
            </Typography>
            {file && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                Selected file: {file.name}
              </Typography>
            )}
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".xlsx,.xls"
            onChange={handleFileChange}
          />

          <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
            <Button
              className="btn-outlined"
              startIcon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              disabled={loading || uploading}
            >
              Download Template
            </Button>
            <Button
              className="btn-outlined"
              startIcon={
                loading ? (
                  <CircularProgress size={18} />
                ) : (
                  <UploadFileOutlined />
                )
              }
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || uploading}
            >
              {loading ? "Processing..." : "Choose Excel"}
            </Button>
            {rows.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutline />}
                onClick={clear}
                disabled={uploading}
              >
                Clear
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              className="btn-contained"
              startIcon={
                uploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <UploadFileOutlined />
                )
              }
              onClick={handleSubmit}
              disabled={uploading || loading || rows.length === 0}
            >
              {uploading ? "Uploading..." : "Submit Report"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {rows.length > 0 && (
        <>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 2, justifyContent: "center" }}
          >
            <Chip label={`Rows: ${rows.length}`} variant="outlined" />
            <Chip
              label={`Columns: ${config.columns.length}`}
              variant="outlined"
            />
          </Stack>
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #e3e7ef",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #e3e7ef" }}>
              <Typography sx={{ fontWeight: 700 }}>Preview</Typography>
              <Typography variant="body2" color="text.secondary">
                Review the imported rows before submitting the report.
              </Typography>
            </Box>
            <DataGrid
              rows={rows}
              columns={gridColumns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              sx={{
                border: 0,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f7f9fc",
                  fontWeight: 700,
                },
                "& .MuiDataGrid-cell": { fontSize: "0.875rem" },
                "& .MuiDataGrid-row:hover": { backgroundColor: "#fafbfc" },
              }}
            />
          </Paper>
        </>
      )}
    </Box>
  );
}

const CONFIGS = {
  openPR: PR_CONFIG,
  openPO: PO_CONFIG,
  delivery: DELIVERY_CONFIG,
};

const UploadReports = ({ reportType }) => {
  const config = CONFIGS[reportType];

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: "#172033", mb: 0.5 }}
        >
          {config.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {config.description}
        </Typography>
      </Box>

      <ExcelUploadPanel key={reportType} config={config} />
    </Box>
  );
};

export default UploadReports;
